import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Insights from './pages/Insights';
import Contact from './pages/Contact';
import Payment from './pages/Payment';
import ScrollToTop from './components/ScrollToTop';
import ItrCalculator from './pages/ItrCalculator';

import StartupRegistration from './pages/StartupRegistration';
import GstServices from './pages/GstServices';
import TaxPlanning from './pages/TaxPlanning';
import McaCompliance from './pages/McaCompliance';

import TaxTools from './pages/TaxTools';
import DocumentVault from './pages/DocumentVault';
import WhatsAppFloat from './components/WhatsAppFloat';
import { AIChatbot } from './components/AIChatbot';
import { AuthProvider } from './context/AuthContext';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ErrorBoundary from './components/ErrorBoundary';

// Resilient code-split loader that auto-recovers from ChunkLoadError
const lazyWithRetry = (importFn: () => Promise<any>, namedExport?: string) =>
  React.lazy(async () => {
    try {
      const module = await importFn();
      if (namedExport && module[namedExport]) {
        return { default: module[namedExport] };
      }
      if (module.default) {
        return module;
      }
      return { default: Object.values(module)[0] as React.ComponentType<any> };
    } catch (error: any) {
      console.warn('Chunk load error caught, attempting auto-recovery...', error);
      const isRetried = sessionStorage.getItem('chunk_retry_' + window.location.pathname);
      if (!isRetried) {
        sessionStorage.setItem('chunk_retry_' + window.location.pathname, 'true');
        window.location.reload();
        return { default: () => null };
      }
      throw error;
    }
  });

// Code-split portal pages with auto-recovery
const Login = lazyWithRetry(() => import('./pages/Login'), 'Login');
const StaffLogin = lazyWithRetry(() => import('./pages/StaffLogin'), 'StaffLogin');
const EmployeePortal = lazyWithRetry(() => import('./pages/EmployeePortal'), 'EmployeePortal');
const ClientDashboard = lazyWithRetry(() => import('./pages/ClientDashboard'), 'ClientDashboard');
const SalesPortal = lazyWithRetry(() => import('./pages/SalesPortal'), 'SalesPortal');
const AdminPublish = lazyWithRetry(() => import('./pages/AdminPublish'));

const AppContent: React.FC = () => {
  const location = useLocation();

  // Internal employee CRM path detection (obscured routes)
  const isInternalPortal = location.pathname.startsWith('/fp-');
  const isClientDashboard = location.pathname === '/client-dashboard' || location.pathname === '/client-portal';

  const hideFooter = isInternalPortal || isClientDashboard;
  const hideNavbar = isInternalPortal;

  // Suspense fallback for lazy-loaded portal chunks
  const PortalSuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 40, border: '3px solid #E2E8F0', borderTop: '3px solid #123D6B', borderRadius: '50%', animation: 'spin 1s linear infinite', mx: 'auto', mb: 2 }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
      </Box>
    }>
      {children}
    </Suspense>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      {!hideNavbar && <Navbar />}
      <ScrollToTop />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/service/:serviceId" element={<ServiceDetail />} />
          <Route path="/tax-tools" element={<TaxTools />} />
          <Route path="/tools" element={<TaxTools />} />
          <Route path="/resources" element={<Insights />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/pay-online" element={<Payment />} />
          <Route path="/pay" element={<Payment />} />
          <Route path="/pay/:token" element={<Payment />} />
          <Route path="/itr-calculator" element={<ItrCalculator />} />
          <Route path="/startup-registration" element={<StartupRegistration />} />
          <Route path="/gst-services" element={<GstServices />} />
          <Route path="/tax-planning" element={<TaxPlanning />} />
          <Route path="/mca-compliance" element={<McaCompliance />} />
          <Route path="/document-vault" element={<DocumentVault />} />

          {/* Client Authentication & Dashboard (100% Client-Facing) */}
          <Route path="/login" element={<PortalSuspense><Login /></PortalSuspense>} />
          <Route path="/client-login" element={<PortalSuspense><Login /></PortalSuspense>} />
          <Route path="/signup" element={<PortalSuspense><Login initialMode="signup" /></PortalSuspense>} />
          <Route path="/register" element={<PortalSuspense><Login initialMode="signup" /></PortalSuspense>} />
          <Route path="/portal" element={<PortalSuspense><Login /></PortalSuspense>} />
          <Route path="/client-dashboard" element={<PortalSuspense><ClientDashboard /></PortalSuspense>} />
          <Route path="/client-portal" element={<PortalSuspense><ClientDashboard /></PortalSuspense>} />

          {/* Internal Employee CRM & Staff Portal — Obscured & Isolated */}
          <Route path="/fp-staff-login-9k7m" element={<PortalSuspense><StaffLogin /></PortalSuspense>} />
          <Route path="/fp-desk-9k7m" element={<PortalSuspense><EmployeePortal /></PortalSuspense>} />
          <Route path="/fp-sales-9k7m" element={<PortalSuspense><SalesPortal /></PortalSuspense>} />
          <Route path="/fp-studio-9k7m" element={<PortalSuspense><AdminPublish /></PortalSuspense>} />

          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* 404 Catch-All */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
      {!isInternalPortal && (
        <>
          <WhatsAppFloat />
          <AIChatbot />
        </>
      )}
      {!hideFooter && <Footer />}
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
